import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';

import {
  Grid,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  LinearProgress,
  Typography,
} from '@material-ui/core';
import {
  isArray,
  isEmpty,
  concat,
  differenceBy,
  includes,
  isEqual,
  findIndex,
} from 'lodash';
import PropTypes from 'prop-types';
import qs from 'querystring';

import { Table } from '~/components';
import { t } from '~/helpers';
import api from '~/services/api';

import * as S from './styles';

export default function ListTransfer({
  url,
  transfer,
  tree,
  maxWidth,
  maxHeight,
  label,
  unique,
  onCallback,
  grid,
  cells,
  search,
  query,
  reset,
  leftTitle,
  rightTitle,
  showCount,
  formatter,
  paginate,
  showPaginate,
  start,
  move,
}) {
  const [data, setData] = useState([]);
  const [list, setList] = useState(start);
  const [listLeft, setListLeft] = useState([]);
  const [listRight, setListRight] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleGetResponse = useCallback(async () => {
    try {
      setLoading(true);

      const response = await api.get(url, {
        params: { pagesize: 99999, ...query, pesquisalista: true },
        paramsSerializer: (param) => qs.stringify(param),
      });

      if (tree)
        setData(differenceBy(formatter(response.data.items), list, unique));
      else setData(differenceBy(formatter(response.data), list, unique));
    } catch (err) {
      if (isArray(err)) err.map((e) => toast.error(t(e)));
      else toast.error(t(err));
    } finally {
      setLoading(false);
    }
  }, [tree, url, query]);

  const handleToggleItem = useCallback((list, value, checked) => {
    if (list === 'toRight') {
      if (checked)
        setListLeft((state) => state.filter((s) => !isEqual(s, value)));
      else setListLeft((state) => [...state, value]);
    }

    if (list === 'toLeft') {
      if (checked)
        setListRight((state) => state.filter((s) => !isEqual(s, value)));
      else setListRight((state) => [...state, value]);
    }
  }, []);

  const handleMoveToRight = useCallback(() => {
    const con = [...list, ...listLeft];
    // console.log(
    //   con.filter((item, idx) => con.indexOf(item) === idx),
    //   'valores concatenados novos'
    // );
    // console.log(concat(list, listLeft), 'valores concatenados');
    // console.log(differenceBy(list, listLeft, unique), 'valores diferenciados');

    setList(() => con.filter((item, idx) => con.indexOf(item) === idx));
    setData((state) => differenceBy(state, listLeft, unique));
    setListLeft([]);
  }, [listLeft, unique]);

  const handleMoveToLeft = useCallback(() => {
    setData((state) => concat(state, listRight));
    setList((state) => differenceBy(state, listRight, unique));
    setListRight([]);
  }, [listRight, unique]);

  const handleMoveAllToRight = useCallback(() => {
    setList((state) => concat(state, data));
    setData([]);
    setListLeft([]);
  }, [data]);

  const handleMoveAllToLeft = useCallback(() => {
    setData((state) => concat(state, list));
    setList([]);
    setListRight([]);
  }, [list]);

  const handleMoveUp = useCallback(() => {
    const index = findIndex(list, listRight[0]);
    const items = [...list];

    if (index > 0) {
      items.splice(index, 1);
      items.splice(index - 1, 0, listRight[0]);
    }

    setList(items);
  }, [listRight, list]);

  const handleMoveDown = useCallback(() => {
    const index = findIndex(list, listRight[0]);
    const items = [...list];

    if (index < items.length - 1) {
      items.splice(index, 1);
      items.splice(index + 1, 0, listRight[0]);
    }

    setList(items);
  }, [listRight, list]);

  useEffect(() => {
    if (url) handleGetResponse();
  }, [url, query]);

  useEffect(() => {
    onCallback(list);
  }, [list]);

  useEffect(() => {
    if (reset) setList([]);
  }, [reset]);

  return (
    <S.Wrapper container spacing={2} justify="center" alignItems="flex-start">
      <Grid item>
        {leftTitle && (
          <Typography
            variant="subtitle2"
            style={{
              textTransform: 'uppercase',
              fontWeight: 'bold',
              margin: '0 1rem',
            }}
          >
            {leftTitle}
          </Typography>
        )}
        <S.WrapperPaper maxWidth={maxWidth} grid={grid}>
          {loading && <LinearProgress />}

          {grid ? (
            <Table
              checkbox
              noButton
              search={search}
              cells={cells}
              maxHeight={maxHeight}
              data={data}
              onCallback={(value) => setListLeft(value)}
              clearCheckeds={isEmpty(listLeft)}
              paginate={paginate}
              size="small"
              showCount={showCount}
              showPagination={showPaginate}
            />
          ) : (
            <List
              dense
              component="div"
              role="list"
              style={{ marginTop: '2rem' }}
            >
              {data.map((item, key) => {
                const isCheck = includes(listLeft, item);
                const isUnique = `transfer-list-item-${key}-left`;

                return (
                  <ListItem
                    key={key}
                    role="listitem"
                    button
                    onClick={() => handleToggleItem('toRight', item, isCheck)}
                  >
                    <ListItemIcon>
                      <Checkbox
                        checked={includes(listLeft, item)}
                        tabIndex={-1}
                        disableRipple
                        inputProps={{ 'aria-labelledby': isUnique }}
                      />
                    </ListItemIcon>
                    <ListItemText id={isUnique} primary={item[label]} />
                  </ListItem>
                );
              })}
              <ListItem />
            </List>
          )}
        </S.WrapperPaper>
      </Grid>
      <Grid item style={{ alignSelf: 'center' }}>
        <S.WrapperButtons container direction="column" alignItems="center">
          {transfer === 'complete' && (
            <Button
              variant="outlined"
              size="small"
              disabled={isEmpty(data)}
              onClick={handleMoveAllToRight}
            >
              ≫
            </Button>
          )}

          <Button
            variant="outlined"
            size="small"
            disabled={isEmpty(listLeft)}
            onClick={handleMoveToRight}
          >
            &gt;
          </Button>

          <Button
            variant="outlined"
            size="small"
            disabled={isEmpty(listRight)}
            onClick={handleMoveToLeft}
          >
            &lt;
          </Button>

          {transfer === 'complete' && (
            <Button
              variant="outlined"
              size="small"
              disabled={isEmpty(list)}
              onClick={handleMoveAllToLeft}
            >
              ≪
            </Button>
          )}

          {move && (
            <>
              <Button
                variant="outlined"
                size="small"
                disabled={isEmpty(listRight) || listRight.length > 1}
                onClick={handleMoveUp}
              >
                &and;
              </Button>

              <Button
                variant="outlined"
                size="small"
                disabled={isEmpty(listRight) || listRight.length > 1}
                onClick={handleMoveDown}
              >
                &or;
              </Button>
            </>
          )}
        </S.WrapperButtons>
      </Grid>
      <Grid item>
        {rightTitle && (
          <Typography
            variant="subtitle2"
            style={{
              textTransform: 'uppercase',
              fontWeight: 'bold',
              margin: '0 1rem',
            }}
          >
            {rightTitle}
          </Typography>
        )}
        <S.WrapperPaper maxWidth={maxWidth} grid={grid}>
          {grid ? (
            <Table
              checkbox
              noButton
              search={search}
              cells={cells}
              maxHeight={maxHeight}
              data={list}
              onCallback={(value) => setListRight(value)}
              clearCheckeds={isEmpty(listRight)}
              paginate={paginate}
              size="small"
              showCount={showCount}
              showPagination={showPaginate}
            />
          ) : (
            <List
              dense
              component="div"
              role="list"
              style={{ marginTop: '2rem' }}
            >
              {list.map((item, key) => {
                const isCheck = includes(listRight, item);
                const isUnique = `transfer-list-item-${key}-right`;

                return (
                  <ListItem
                    key={key}
                    role="listitem"
                    button
                    onClick={() => handleToggleItem('toLeft', item, isCheck)}
                  >
                    <ListItemIcon>
                      <Checkbox
                        checked={includes(listRight, item)}
                        tabIndex={-1}
                        disableRipple
                        inputProps={{ 'aria-labelledby': isUnique }}
                      />
                    </ListItemIcon>
                    <ListItemText id={isUnique} primary={item[label]} />
                  </ListItem>
                );
              })}
              <ListItem />
            </List>
          )}
        </S.WrapperPaper>
      </Grid>
    </S.Wrapper>
  );
}

ListTransfer.propTypes = {
  url: PropTypes.string.isRequired,
  tree: PropTypes.bool,
  cells: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      align: PropTypes.string,
      style: PropTypes.func,
      format: PropTypes.func,
      type: PropTypes.string,
      configure: PropTypes.objectOf(
        PropTypes.shape({
          true: PropTypes.string,
          false: PropTypes.string,
        })
      ),
    })
  ),
  maxWidth: PropTypes.number,
  maxHeight: PropTypes.number,
  label: PropTypes.string.isRequired,
  unique: PropTypes.string.isRequired,
  onCallback: PropTypes.func.isRequired,
  transfer: PropTypes.oneOf(['simple', 'complete']),
  grid: PropTypes.bool,
  search: PropTypes.bool,
  query: PropTypes.objectOf(PropTypes.any),
  reset: PropTypes.bool,
  leftTitle: PropTypes.string,
  rightTitle: PropTypes.string,
  showCount: PropTypes.bool,
  formatter: PropTypes.func,
  paginate: PropTypes.number,
  showPaginate: PropTypes.bool,
  start: PropTypes.arrayOf(PropTypes.any),
  move: PropTypes.bool,
};

ListTransfer.defaultProps = {
  tree: true,
  grid: false,
  transfer: 'simple',
  maxWidth: 21,
  maxHeight: 440,
  cells: [],
  start: [],
  search: false,
  query: {},
  reset: false,
  leftTitle: '',
  rightTitle: '',
  showCount: false,
  formatter: (value) => value,
  paginate: 20,
  showPaginate: true,
  move: false,
};
